import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { BRANDING_CONFIG, DEFAULT_BRANDING } from '../branding.config';
import { IBranding } from '../interfaces/branding.interface';

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly document = inject(DOCUMENT);

  private readonly branding = signal<IBranding>(DEFAULT_BRANDING);

  public readonly logoUrl = computed(() => this.branding().logoUrl);
  public readonly primaryColor = computed(() => this.branding().primaryColor);

  public applyBranding(): void {
    const branding = this.resolveBranding();
    this.branding.set(branding);

    if (branding.primaryColor) {
      this.applyPrimaryColor(branding.primaryColor);
    }
  }

  // The effective primary colour: the runtime override when a host matched, otherwise the app's
  // compiled Bootstrap default read from the `--bs-primary` custom property.
  public resolvePrimaryColor(): string {
    return this.primaryColor() ?? this.readCompiledPrimaryColor();
  }

  private resolveBranding(): IBranding {
    return BRANDING_CONFIG[this.resolveBrandHost()] ?? DEFAULT_BRANDING;
  }

  // Normally branding is keyed by the real hostname. The user-manual screenshot pipeline runs the
  // app on the seed domain but needs a customer's branding, so a `?brandHost=` query param (or the
  // same value stashed in sessionStorage by the pipeline) overrides the host lookup. Neither is ever
  // present in normal use, so production keeps resolving by hostname.
  private resolveBrandHost(): string {
    const view = this.document.defaultView;
    const queryHost = view ? new URLSearchParams(view.location.search).get('brandHost') : null;
    return queryHost ?? this.readSessionBrandHost() ?? view?.location.hostname ?? '';
  }

  private readSessionBrandHost(): string | null {
    try {
      return this.document.defaultView?.sessionStorage.getItem('brandHost') ?? null;
    } catch {
      return null;
    }
  }

  private applyPrimaryColor(color: string): void {
    const root = this.document.documentElement;
    const hoverColor = this.shadeColor(color, 0.2);
    const rgb = this.hexToRgb(color);
    const hoverRgb = this.hexToRgb(hoverColor);

    root.style.setProperty('--primary', color);
    root.style.setProperty('--bs-primary', color);
    // Links and .btn-link derive their colour from the Bootstrap link vars (compiled from $primary),
    // so override those too or link-styled UI keeps the compiled default colour.
    root.style.setProperty('--bs-link-color', color);
    root.style.setProperty('--bs-link-hover-color', hoverColor);
    // The primary "subtle" family drives .bg-primary-subtle and every .alert-primary (bg/border/text);
    // it's compiled from $primary, so tint it from the runtime colour to keep those on-brand.
    root.style.setProperty('--bs-primary-bg-subtle', `color-mix(in srgb, ${color}, #fff 80%)`);
    root.style.setProperty('--bs-primary-border-subtle', `color-mix(in srgb, ${color}, #fff 60%)`);
    root.style.setProperty('--bs-primary-text-emphasis', `color-mix(in srgb, ${color}, #000 40%)`);

    if (rgb) {
      root.style.setProperty('--bs-primary-rgb', rgb);
      root.style.setProperty('--bs-link-color-rgb', rgb);
    }

    if (hoverRgb) {
      root.style.setProperty('--bs-link-hover-color-rgb', hoverRgb);
    }
  }

  private readCompiledPrimaryColor(): string {
    const view = this.document.defaultView;
    if (!view) {
      return '';
    }

    return view.getComputedStyle(this.document.documentElement).getPropertyValue('--bs-primary').trim();
  }

  private shadeColor(hex: string, amount: number): string {
    const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
    if (!match) {
      return hex;
    }

    const shade = (channel: string): string =>
      Math.round(parseInt(channel, 16) * (1 - amount))
        .toString(16)
        .padStart(2, '0');

    return `#${shade(match[1])}${shade(match[2])}${shade(match[3])}`;
  }

  private hexToRgb(hex: string): string | null {
    const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
    if (!match) {
      return null;
    }

    const red = parseInt(match[1], 16);
    const green = parseInt(match[2], 16);
    const blue = parseInt(match[3], 16);

    return `${red}, ${green}, ${blue}`;
  }
}
